import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreateOutputStorage, useUpdateOutputStorage } from '@/api/media/shared-storage';
import type { FileTransferConfig } from '@/api/media/configs';
import type { CreateOutputStoragePayload } from '@/api/media/shared-storage-types';
import type {
  StorageKind,
  StorageDraftMap,
  StorageFormErrorMap,
  EditRow,
} from './storage-form-types';
import { DEFAULT_DRAFTS } from './storage-form-types';

const URLISH_RE = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z0-9-]+(:\d+)?(\/.*)?$/i;
const trim = (v: string) => v.trim();
const isRequired = (v: string) => trim(v).length > 0;
const isUrlLike = (v: string) => URLISH_RE.test(trim(v)) || /^https?:\/\//i.test(trim(v));
const toPort = (v: string) => Number.parseInt(v, 10);
const normalise = (v: string) =>
  trim(v).replace(/^(https?:\/\/)+/i, (m) => (m.toLowerCase().includes('https://') ? 'https://' : 'http://'));
const ensureScheme = (v: string, scheme: 'http' | 'https') => {
  const n = normalise(v).replace(/^https?:\/\//i, '');
  return n ? `${scheme}://${n}` : '';
};
const ensureAnyScheme = (v: string, fallback: 'http' | 'https' = 'https') => {
  const n = normalise(v);
  if (!n) return '';
  return /^https?:\/\//i.test(n) ? n : `${fallback}://${n}`;
};

function cloneDrafts(): StorageDraftMap {
  return {
    s3: { ...DEFAULT_DRAFTS.s3 },
    ftp: { ...DEFAULT_DRAFTS.ftp },
    sftp: { ...DEFAULT_DRAFTS.sftp },
    http: { ...DEFAULT_DRAFTS.http },
    webdav: { ...DEFAULT_DRAFTS.webdav },
  };
}

// Secret fields are always seeded as '' so they are never echoed back to the API.
// On edit, leaving a secret blank means "keep the stored value" (leave-blank-to-keep).
function validate(type: StorageKind, draft: StorageDraftMap[StorageKind], isEditMode = false): StorageFormErrorMap {
  const errors: StorageFormErrorMap = {};
  const req = (f: string, v: string, msg: string) => { if (!isRequired(v)) errors[f] = msg; };
  const port = (f: string, v: string) => { const p = toPort(v); if (!Number.isInteger(p) || p < 1 || p > 65535) errors[f] = 'Port must be 1–65535.'; };
  const url = (f: string, v: string) => { if (!isRequired(v) || !isUrlLike(v)) errors[f] = 'Enter a valid hostname or URL.'; };

  switch (type) {
    case 's3': {
      const s = draft as StorageDraftMap['s3'];
      req('name', s.name, 'Name is required.');
      req('access_key_id', s.access_key_id, 'Access key ID is required.');
      if (!isEditMode || s.secret_access_key) req('secret_access_key', s.secret_access_key, 'Secret access key is required.');
      req('region', s.region, 'Region is required.');
      req('bucket', s.bucket, 'Bucket is required.');
      if (s.useCustomEndpoint) {
        req('endpoint', s.endpoint, 'Endpoint is required.');
        if (isRequired(s.endpoint) && !/^https?:\/\//i.test(trim(s.endpoint))) errors.endpoint = 'Endpoint must be a full URL.';
      }
      break;
    }
    case 'ftp': {
      const f = draft as StorageDraftMap['ftp'];
      req('name', f.name, 'Name is required.'); url('host', f.host); port('port', f.port);
      req('username', f.username, 'Username is required.');
      if (!isEditMode || f.password) req('password', f.password, 'Password is required.');
      break;
    }
    case 'sftp': {
      const s = draft as StorageDraftMap['sftp'];
      req('name', s.name, 'Name is required.'); url('host', s.host); port('port', s.port);
      req('username', s.username, 'Username is required.');
      if (!isEditMode || s.password) req('password', s.password, 'Password is required.');
      break;
    }
    case 'http': {
      const h = draft as StorageDraftMap['http'];
      req('name', h.name, 'Name is required.');
      if (!isRequired(h.host) || !/^https?:\/\//i.test(trim(h.host))) errors.host = 'Enter a valid URL (http:// or https://).';
      if (h.auth_type === 'basic') {
        req('username', h.username, 'Username required.');
        if (!isEditMode || h.password) req('password', h.password, 'Password required.');
      }
      break;
    }
    case 'webdav': {
      const w = draft as StorageDraftMap['http'];
      req('name', w.name, 'Name is required.');
      if (!isRequired(w.host) || !/^http:\/\//i.test(trim(w.host))) errors.host = 'WebDAV URL must start with http://.';
      if (w.auth_type === 'basic') {
        req('username', w.username, 'Username required.');
        if (!isEditMode || w.password) req('password', w.password, 'Password required.');
      }
      break;
    }
  }
  return errors;
}

// Omit secret fields from the payload when blank in edit mode — API keeps stored value.
function buildPayload(type: StorageKind, draft: StorageDraftMap[StorageKind], isEditMode = false): CreateOutputStoragePayload {
  switch (type) {
    case 's3': {
      const s = draft as StorageDraftMap['s3'];
      const config: Record<string, unknown> = { name: s.name.trim(), access_key_id: s.access_key_id.trim(), region: s.region.trim(), bucket: s.bucket.trim() };
      if (s.secret_access_key || !isEditMode) config.secret_access_key = s.secret_access_key;
      if (s.useCustomEndpoint && s.endpoint.trim()) config.endpoint = s.endpoint.trim();
      return { type: 's3', config } as unknown as CreateOutputStoragePayload;
    }
    case 'ftp': {
      const f = draft as StorageDraftMap['ftp'];
      const config: Record<string, unknown> = { name: f.name.trim(), host: f.host.trim(), port: toPort(f.port), username: f.username.trim(), pasv: f.pasv };
      if (f.password || !isEditMode) config.password = f.password;
      return { type: 'ftp', config } as unknown as CreateOutputStoragePayload;
    }
    case 'sftp': {
      const s = draft as StorageDraftMap['sftp'];
      const config: Record<string, unknown> = { name: s.name.trim(), host: s.host.trim(), port: toPort(s.port), username: s.username.trim() };
      if (s.password || !isEditMode) config.password = s.password;
      return { type: 'sftp', config } as unknown as CreateOutputStoragePayload;
    }
    case 'http':
    case 'webdav': {
      const h = draft as StorageDraftMap['http'];
      const config: Record<string, unknown> = { name: h.name.trim(), host: type === 'webdav' ? ensureScheme(h.host, 'http') : ensureAnyScheme(h.host, 'https'), auth_type: h.auth_type === 'basic' ? 2 : 1 };
      if (h.auth_type === 'basic') {
        config.username = h.username.trim();
        if (h.password || !isEditMode) config.password = h.password;
      }
      return { type, config } as unknown as CreateOutputStoragePayload;
    }
  }
}

function seedFromRow(row: EditRow): { kind: StorageKind; drafts: StorageDraftMap } {
  const kind = row.type.toLowerCase() as StorageKind;
  const r = row.raw;
  const base = cloneDrafts();
  switch (kind) {
    // Secrets are always seeded as '' — leave-blank-to-keep semantics on edit.
    case 's3': base.s3 = { ...base.s3, name: r.name || '', access_key_id: r.access_key_id || '', secret_access_key: '', region: r.region || '', bucket: r.bucket || '', endpoint: r.endpoint || '', useCustomEndpoint: !!r.endpoint }; break;
    case 'ftp': base.ftp = { ...base.ftp, name: r.name || '', host: r.host || '', port: String(r.port ?? '21'), username: r.username || '', password: '', pasv: r.pasv ?? true }; break;
    case 'sftp': base.sftp = { ...base.sftp, name: r.name || '', host: r.host || '', port: String(r.port ?? '22'), username: r.username || '', password: '' }; break;
    case 'http': case 'webdav': base[kind] = { ...base[kind], name: r.name || '', host: r.host || '', auth_type: r.auth_type === 2 ? 'basic' : 'none', username: r.username || '', password: '' }; break;
  }
  return { kind, drafts: base };
}

export function useStorageForm(
  serviceId: string,
  config: FileTransferConfig,
  onDone: () => void,
  editRow?: EditRow,
) {
  const { show } = useToast();
  const isEditMode = !!editRow;

  const [activeType, setActiveTypeState] = useState<StorageKind>('s3');
  const [drafts, setDrafts] = useState<StorageDraftMap>(cloneDrafts);

  useEffect(() => {
    if (editRow) {
      const { kind, drafts: seeded } = seedFromRow(editRow);
      setActiveTypeState(kind);
      setDrafts(seeded);
    } else {
      setActiveTypeState('s3');
      setDrafts(cloneDrafts());
    }
  }, [editRow]);

  const create = useCreateOutputStorage(serviceId, config, {
    onSuccess: () => { show({ tone: 'success', title: 'Storage created' }); onDone(); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to create storage' }),
  });

  const update = useUpdateOutputStorage(serviceId, config, {
    onSuccess: () => { show({ tone: 'success', title: 'Storage updated' }); onDone(); },
    onError: (e) => show({ tone: 'danger', title: e.message || 'Failed to update storage' }),
  });

  const isSubmitting = create.isPending || update.isPending;
  const currentDraft = drafts[activeType];
  const currentErrors = useMemo(() => validate(activeType, currentDraft, isEditMode), [activeType, currentDraft, isEditMode]);

  const updateField = (field: string, value: string | boolean) => {
    setDrafts((prev) => {
      const next = { ...prev, [activeType]: { ...prev[activeType] } };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (next[activeType] as unknown as Record<string, string | boolean>)[field] = value;
      if (activeType === 's3' && field === 'useCustomEndpoint' && !value) {
        (next.s3 as StorageDraftMap['s3']).endpoint = DEFAULT_DRAFTS.s3.endpoint;
      }
      return next;
    });
  };

  const setActiveType = (t: StorageKind) => {
    if (!isEditMode) setActiveTypeState(t);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errs = validate(activeType, currentDraft, isEditMode);
    if (Object.keys(errs).length > 0) {
      show({ tone: 'danger', title: 'Fix the highlighted fields before submitting.' });
      return;
    }
    const payload = buildPayload(activeType, currentDraft, isEditMode);
    if (isEditMode && editRow) {
      await update.mutateAsync({ outputStorageType: activeType, outputStorageId: String(editRow.originalId), payload });
    } else {
      await create.mutateAsync(payload);
    }
  };

  return { activeType, currentDraft, currentErrors, isSubmitting, isEditMode, setActiveType, updateField, handleSubmit };
}
